import { CruiseGroupPaymentTerms } from "@silversea-ssc/dotcom-api-types/CruiseGroup";
import { EnhancedCancellationPeriod } from "@silversea-ssc/dotcom-api-types/PaymentPeriods";
import {
  CountryPaymentTermsResponse,
  PaymentTermsResponse
} from "features/cancellation-policy/useRemotePaymentTerms";
import { match } from "ts-pattern";
import { NON_BREKING_SPACE } from "./strings";
import { Currency } from "./localization/currency";
import { formatI18nCurrency } from "./localization/formatCurrency";
import { RemotePaymentTermFareResponse } from "features/cancellation-policy/helpers";
import { Refundability } from "@silversea-ssc/dotcom-read-model-types/Common";
import { AdministrativeFee } from "@silversea-ssc/dotcom-read-model-types/CruisePaymentTerms";
import { LinguiT, maybeTranslation } from "./localization/lingui-helpers";
import { msg } from "@lingui/core/macro";
import { MessageDescriptor } from "@lingui/core";

type RefundabilityInfo = {
  isRefundable: boolean | undefined;
  content: string;
};

export const currentCancellationPeriod = (
  terms:
    | PaymentTermsResponse
    | RemotePaymentTermFareResponse<CruiseGroupPaymentTerms>
): EnhancedCancellationPeriod | undefined => {
  return "currentCancellationPeriod" in terms
    ? terms.currentCancellationPeriod
    : terms.cancellationPeriods?.[0];
};

export const fareDepositRefundability = (
  t: LinguiT,
  terms:
    | PaymentTermsResponse
    | RemotePaymentTermFareResponse<CruiseGroupPaymentTerms>
    | undefined
): RefundabilityInfo & { administrativeFeeDisclaimer?: string } => {
  if (!terms) return { isRefundable: undefined, content: NON_BREKING_SPACE };
  const withCurrent = "currentCancellationPeriod" in terms;

  const period = currentCancellationPeriod(terms);

  const refundableUntilDaysBeforeDeparture =
    period?.validUntilDaysBeforeDeparture;
  const percentage = cancellationPeriodPercentage(period);

  const fareDepositRefundabilityLabelValue = fareDepositRefundabilityLabel(
    t,
    refundabilityType(withCurrent, terms, period),
    refundableUntilDaysBeforeDeparture,
    percentage
  );

  const administrativeFeeDisclaimer = isFullyRefundable(
    terms.depositType,
    period
  )
    ? formattedAdministrativeFeeDisclaimer(
        terms.cancellationAdminFee,
        terms.currency
      )
    : undefined;

  return {
    ...fareDepositRefundabilityLabelValue,
    administrativeFeeDisclaimer: maybeTranslation(
      t,
      administrativeFeeDisclaimer
    )
  };
};

const cancellationPeriodPercentage = (
  currentCancellationPeriod: EnhancedCancellationPeriod | undefined
): number =>
  match(currentCancellationPeriod)
    .with(
      { type: "Proportion", penalty: { type: "percentage" } },
      (props) => props.penalty.percentage
    )
    .with(
      { type: "Proportion", penalty: { type: "percentageWithMaximumAmount" } },
      (props) => props.penalty.percentage
    )
    .otherwise(() => 0);

const fareDepositRefundabilityLabel = (
  t: LinguiT,
  depositType: CountryPaymentTermsResponse["depositType"] | undefined,
  refundableUntilDaysBeforeDeparture = 1,
  percentage = 0
): RefundabilityInfo => {
  if (!depositType)
    return { isRefundable: undefined, content: NON_BREKING_SPACE };

  const days = refundableUntilDaysBeforeDeparture - 1;
  const depositPercentage = 100 - percentage;
  switch (depositType) {
    case "refundable": {
      return percentage === 0
        ? {
            isRefundable: true,
            content: t(
              msg({
                id: "deposit-refundable-content-new-asterisk",
                message: `Deposit ${depositPercentage}%* refundable until  ${days} days pre cruise`
              })
            )
          }
        : {
            isRefundable: true,
            content: t(
              msg({
                id: "deposit-refundable-content-new",
                message: `Deposit ${depositPercentage}% refundable until  ${days} days pre cruise`
              })
            )
          };
    }

    case "nonRefundable":
      return {
        isRefundable: false,
        content: t(
          msg({
            id: "deposit-non-refundable-content",
            message: `Deposit not refundable`
          })
        )
      };

    case "exchangeableForCredit":
      return {
        isRefundable: false,
        content: t(
          msg({
            id: "deposit-exchangeable-for-credit-content",
            message: `Deposit convertible to FCC*`
          })
        )
      };
  }
};

export const isFullyRefundable = (
  depositType: Refundability | undefined,
  currentCancellationPeriod: EnhancedCancellationPeriod | undefined
) => {
  const percentage = cancellationPeriodPercentage(currentCancellationPeriod);
  //   return depositType !== "exchangeableForCredit" && percentage === 0;

  if (depositType == "nonRefundable") return false;

  return (
    depositType !== "exchangeableForCredit" &&
    currentCancellationPeriod?.type === "Proportion" &&
    percentage === 0
  );
};

export const isPartiallyRefundable = (
  depositType: Refundability | undefined,
  currentCancellationPeriod: EnhancedCancellationPeriod | undefined
) => {
  const percentage = cancellationPeriodPercentage(currentCancellationPeriod);
  //   return depositType !== "exchangeableForCredit" && percentage === 0;

  if (depositType == "nonRefundable") return false;

  return (
    depositType !== "exchangeableForCredit" &&
    currentCancellationPeriod?.type === "Proportion" &&
    percentage < 100 &&
    percentage > 0
  );
};

const refundabilityType = (
  withCurrent: boolean,
  terms:
    | PaymentTermsResponse
    | RemotePaymentTermFareResponse<CruiseGroupPaymentTerms>,
  currentCancellationPeriod: EnhancedCancellationPeriod | undefined
) => {
  if (!withCurrent) return terms.depositType;

  if (isExchangeableForCredit(terms, currentCancellationPeriod))
    return "exchangeableForCredit";

  return currentCancellationPeriod?.type === "NonRefundable"
    ? "nonRefundable"
    : "refundable";
};

const isExchangeableForCredit = (
  terms:
    | PaymentTermsResponse
    | RemotePaymentTermFareResponse<CruiseGroupPaymentTerms>,
  currentCancellationPeriod: EnhancedCancellationPeriod | undefined
) =>
  terms.depositType === "exchangeableForCredit" &&
  currentCancellationPeriod?.type !== "NonRefundable";

const formattedAdministrativeFeeDisclaimer = (
  value: AdministrativeFee | undefined,
  currency: Currency | undefined
): MessageDescriptor | undefined => {
  if (!value || !currency) return undefined;

  const amount = formatI18nCurrency(value.amount, currency);
  if (value.amount <= 0) return undefined;

  switch (value.type) {
    case "fixedAmountPerBooking": {
      return msg({
        id: "administrative-fee-per-booking-disclaimer",
        message: `*Includes a ${amount} administrative fee per booking, which will be automatically converted to a Future Cruise Credit (FCC)`
      });
    }

    case "fixedAmountPerPerson": {
      return msg({
        id: "administrative-fee-per-person-disclaimer",
        message: `*Includes a ${amount} administrative fee per person, which will be automatically converted to a Future Cruise Credit (FCC)`
      });
    }
  }
};
